import React, { useEffect, useRef } from 'react';
import { HiChevronDown } from 'react-icons/hi';
import styles from './SelectorDropdown.module.css';

interface SelectorDropdownProps<T> {
  items: T[];
  currentItem: T | null;
  onSelect: (item: T) => void;
  isOpen: boolean;
  onToggle: () => void;
  renderItem: (item: T, isActive: boolean) => React.ReactNode;
  renderCurrentItem: (item: T) => React.ReactNode;
  maxVisibleItems?: number;
  getItemId: (item: T) => string;
  isItemActive?: (item: T, currentItem: T | null) => boolean;
}

function SelectorDropdown<T>({
  items,
  currentItem,
  onSelect,
  isOpen,
  onToggle,
  renderItem,
  renderCurrentItem,
  maxVisibleItems = 3,
  getItemId,
  isItemActive
}: SelectorDropdownProps<T>) {
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        onToggle();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onToggle]);

  if (!currentItem) {
    return null;
  }

  const maxHeight = maxVisibleItems * 49; // ~49px per item

  return (
    <div className={styles.selector} ref={dropdownRef}>
      <button
        className={styles.selectorButton}
        onClick={onToggle}
      >
        <div className={styles.selectorContent}>
          {renderCurrentItem(currentItem)}
        </div>
        {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
        {/* @ts-ignore - react-icons compatibility with React 19 */}
        <HiChevronDown 
          size={20} 
          className={`${styles.selectorChevron} ${isOpen ? styles.selectorChevronOpen : ''}`}
        />
      </button>
      {isOpen && items.length > 0 && (
        <div className={styles.selectorDropdown} style={{ maxHeight: `${maxHeight}px` }}>
          {items.map((item) => {
            const isActive = isItemActive 
              ? isItemActive(item, currentItem)
              : getItemId(item) === getItemId(currentItem);
            return (
              <div key={getItemId(item)}>
                {renderItem(item, isActive)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SelectorDropdown;

